import React, { useEffect, useId, useRef } from 'react';

/**
 * Lightweight in-context overlay for table utilities (history, payments).
 * Mobile: bottom sheet. Desktop: right-side drawer. Esc + backdrop dismiss.
 */
export default function TableUtilityOverlay({ open, title, onClose, children }) {
  const titleId = useId();
  const panelRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const t = window.setTimeout(() => closeRef.current?.focus(), 0);

    return () => {
      window.clearTimeout(t);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="cv2-utility-sheet"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={panelRef}
        className="cv2-utility-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cv2-utility-header">
          <h2 id={titleId} className="cv2-utility-title">
            {title}
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="cv2-utility-close"
            onClick={onClose}
            aria-label={`Close ${title}`}
          >
            ×
          </button>
        </header>
        <div className="cv2-utility-body">{children}</div>
      </div>
    </div>
  );
}
